// 访客记录数据格式化工具
// 将后端原始数据按照图片中的实际列结构进行格式化

import type { VisitorRecord } from '../services/visitor';

// 格式化后的访客记录数据结构
export interface FormattedVisitorRecord {
  id: number;
  link_id: number;
  client_id: number;
  link_code: string;
  link_title?: string;
  referer: string;

  // 请求 & 跳转信息
  request_url: string;
  redirect_url: string;
  redirect_type: string;
  rule_match_result: string;
  access_url: string;

  // 访问状态
  access_status: {
    is_intercepted: boolean;
    intercept_reason: string;
    intercept_type: string;
  };

  // 访问来路
  access_source: string;

  // IP地址信息
  ip_info: {
    ip: string;
    country_code: string;
    country_name: string;
    region: string;
    city: string;
    accessed_at: string;
    timezone: string;
    postal_code: string;
    latitude?: number;
    longitude?: number;
  };

  // IP详情
  ip_details: {
    asn: string;
    asn_name: string;
    asn_type: string;
    asn_route: string;
    asn_org: string;
    is_proxy: boolean;
    is_datacenter: boolean;
    is_satellite: boolean;
    is_vpn: boolean;
    is_tor: boolean;
    is_relay: boolean;
    proxy_type: string;
    risk_level: string;
    network_type: string;
    ip_domain: string;
    ip_hostname: string;
    hosted_domains_count: number;
    company_domain: string;
    company_type: string;
    company_address: string;
    abuse_contact_name: string;
    abuse_contact_email: string;
    abuse_contact_phone: string;
    abuse_contact_address: string;
  };

  // 设备系统
  device_system: {
    device_type: string;
    device_brand: string;
    device_model: string;
    os_type: string;
    os_version: string;
    is_mobile: boolean;
  };

  // 客户端信息
  client_info: {
    client_type: string;
    browser_type: string;
    browser_version: string;
    client_language: string;
    accept_language: string;
    mobile_app: string;
  };

  // User-Agent信息
  user_agent: string;

  // 爬虫信息
  bot_info: {
    is_bot: boolean;
    bot_type: string;
  };

  // 广告平台和规则
  ad_plat?: string;
  rule_id?: number;

  // APP包名
  x_requested_with?: string;

  // 数据来源标记
  use_ipcore: number;      // 是否使用 IPCore 服务，0: 否，1: 是
  use_fingerprint: number; // 是否使用 Fingerprintjs，0: 否，1: 是
  fingerprint_id: string;  // 指纹ID，只有使用指纹接口时才有值

  // 原始数据，方便详情展示
  raw: VisitorRecord;
}

/**
 * 格式化访客记录数据
 * 将后端原始数据按照图片中的实际列结构进行格式化
 */
export function formatVisitorRecord(record: VisitorRecord): FormattedVisitorRecord {
  // 格式化访问时间
  const formatAccessTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return dateString;
    }
  };

  const requestUrl = record.request_url || '';
  const redirectUrl = record.redirect_url || '';
  const accessUrl = redirectUrl || requestUrl || `/${record.link_code}`;

  return {
    id: record.id,
    link_id: record.link_id,
    client_id: record.client_id,
    link_code: record.link_code,
    link_title: record.link_title || '',
    referer: record.referer || '',

    // 请求 & 跳转信息
    request_url: requestUrl,
    redirect_url: redirectUrl,
    redirect_type: record.redirect_type || '',
    rule_match_result: record.rule_match_result || '',
    access_url: accessUrl,

    // 访问状态
    access_status: {
      is_intercepted: Boolean(record.is_intercepted),
      intercept_reason: record.intercept_reason || '',
      intercept_type: record.intercept_type || '',
    },

    // 访问来路
    access_source: record.referer || '',

    // IP地址信息
    ip_info: {
      ip: record.visitor_ip,
      country_code: record.country_code || '',
      country_name: record.country_name || '',
      region: record.region || '',
      city: record.city || '',
      accessed_at: formatAccessTime(record.accessed_at),
      timezone: record.timezone || '',
      postal_code: record.postal_code || '',
      latitude: record.latitude,
      longitude: record.longitude,
    },

    // IP详情
    ip_details: {
      asn: record.asn || '',
      asn_name: record.asn_name || '',
      asn_type: record.asn_type || '',
      asn_route: record.asn_route || '',
      asn_org: record.asn_org || '',
      is_proxy: Boolean(record.is_proxy),
      is_datacenter: Boolean(record.is_datacenter),
      is_satellite: Boolean(record.is_satellite),
      is_vpn: Boolean(record.is_vpn),
      is_tor: Boolean(record.is_tor),
      is_relay: Boolean(record.is_relay),
      proxy_type: record.proxy_type || '',
      risk_level: record.risk_level || '',
      network_type: record.network_type || '',
      ip_domain: record.ip_domain || '',
      ip_hostname: record.ip_hostname || '',
      hosted_domains_count: record.hosted_domains_count ?? 0,
      company_domain: record.company_domain || '',
      company_type: record.company_type || '',
      company_address: record.company_address || '',
      abuse_contact_name: record.abuse_contact_name || '',
      abuse_contact_email: record.abuse_contact_email || '',
      abuse_contact_phone: record.abuse_contact_phone || '',
      abuse_contact_address: record.abuse_contact_address || '',
    },

    // 设备系统
    device_system: {
      device_type: record.device_type || '',
      device_brand: record.device_brand || '',
      device_model: record.device_model || '',
      os_type: record.os_type || '',
      os_version: record.os_version || '',
      is_mobile: Boolean(record.is_mobile),
    },

    // 客户端信息
    client_info: {
      client_type: record.client_type || '',
      browser_type: record.browser_type || '',
      browser_version: record.browser_version || '',
      client_language: record.client_language || '',
      accept_language: record.accept_language || '',
      mobile_app: record.mobile_app || '',
    },

    // User-Agent信息
    user_agent: record.user_agent || '',

    // 爬虫信息
    bot_info: {
      is_bot: Boolean(record.is_bot),
      bot_type: record.bot_type || '',
    },

    // 广告平台和规则
    ad_plat: record.ad_plat,
    rule_id: record.rule_id,

    // APP包名
    x_requested_with: record.x_requested_with,

    // 数据来源标记
    use_ipcore: record.use_ipcore ?? 0,
    use_fingerprint: record.use_fingerprint ?? 0,
    fingerprint_id: record.fingerprint_id || '',

    // 原始数据
    raw: record,
  };
}

/**
 * 格式化访客记录列表
 */
export function formatVisitorRecords(records: VisitorRecord[]): FormattedVisitorRecord[] {
  return records.map(formatVisitorRecord);
}

/**
 * 获取访问状态显示文本
 */
export function getAccessStatusText(accessStatus: FormattedVisitorRecord['access_status']) {
  if (accessStatus.is_intercepted) {
    return '拦截';
  }
  return '正常';
}

/**
 * 获取访问状态显示颜色
 */
export function getAccessStatusColor(accessStatus: FormattedVisitorRecord['access_status']) {
  if (accessStatus.is_intercepted) {
    return '#ff4d4f'; // 红色
  }
  return '#52c41a'; // 绿色
}

/**
 * 获取地理位置显示文本
 */
export function getLocationText(ipInfo: FormattedVisitorRecord['ip_info']) {
  const parts = [];
  if (ipInfo.country_code && ipInfo.country_code !== '') {
    parts.push(ipInfo.country_code.toUpperCase());
  } else if (ipInfo.country_name && ipInfo.country_name !== '') {
    parts.push(ipInfo.country_name);
  }
  if (ipInfo.region && ipInfo.region !== '') parts.push(ipInfo.region);
  if (ipInfo.city && ipInfo.city !== '') parts.push(ipInfo.city);
  
  return parts.length > 0 ? parts.join(' - ') : '未知';
}

/**
 * 获取设备类型显示文本
 */
export function getDeviceTypeText(deviceSystem: FormattedVisitorRecord['device_system']) {
  // 直接从后端下发的常量中获取，不要硬编码
  if (!deviceSystem.device_type || deviceSystem.device_type === '') {
    return '未知';
  }
  
  return deviceSystem.device_type; // 后端已经返回了格式化后的文本
}

/**
 * 获取客户端类型显示文本
 */
export function getClientTypeText(clientInfo: FormattedVisitorRecord['client_info']) {
  // 直接从后端下发的常量中获取，不要硬编码
  if (!clientInfo.client_type || clientInfo.client_type === '') {
    return '未知';
  }
  
  return clientInfo.client_type; // 后端已经返回了格式化后的文本
}

/**
 * 获取网络类型显示文本
 */
export function getNetworkTypeText(ipDetails: FormattedVisitorRecord['ip_details']) {
  // 直接从后端下发的常量中获取，不要硬编码
  if (!ipDetails.network_type) {
    return '未知';
  }
  
  return ipDetails.network_type; // 后端已经返回了格式化后的文本
}

/**
 * 获取风险等级显示文本
 */
export function getRiskLevelText(ipDetails: FormattedVisitorRecord['ip_details']) {
  // 直接从后端下发的常量中获取，不要硬编码
  if (!ipDetails.risk_level) {
    return '未知';
  }
  
  return ipDetails.risk_level; // 后端已经返回了格式化后的文本
}

/**
 * 获取语言显示文本
 */
export function getLanguageText(acceptLanguage: string) {
  // 直接从后端下发的常量中获取，不要硬编码
  if (!acceptLanguage) return '未知';
  
  // 解析Accept-Language头，提取主要语言
  const languages = acceptLanguage.split(',');
  const primaryLanguage = languages[0].trim().split(';')[0];
  
  return primaryLanguage; // 后端会处理语言标签的映射
}

/**
 * 获取网络特征标签配置
 * 直接使用后端返回的数据，不做任何硬编码映射
 */
export function getNetworkFeatureTags(ipDetails: FormattedVisitorRecord['ip_details']) {
  const tags = [];
  
  // 使用后端下发的 proxy_type 来显示代理类型（后端会返回格式化的文本）
  if (ipDetails.is_proxy) {
    tags.push({ 
      text: ipDetails.proxy_type || '代理', 
      color: 'default'
    });
  }

  if (ipDetails.is_vpn) {
    tags.push({
      text: 'VPN',
      color: 'orange',
    });
  }

  if (ipDetails.is_tor) {
    tags.push({
      text: 'Tor',
      color: 'purple',
    });
  }

  if (ipDetails.is_relay) {
    tags.push({
      text: 'Relay',
      color: 'magenta',
    });
  }
  
  // 数据中心标记（后端会返回格式化的文本）
  if (ipDetails.is_datacenter) {
    tags.push({ 
      text: '数据中心', 
      color: 'blue'
    });
  }
  
  if (ipDetails.is_satellite) {
    tags.push({ 
      text: '卫星网络', 
      color: 'cyan'
    });
  }
  
  return tags;
}

/**
 * 获取浏览器类型显示文本
 */
export function getBrowserTypeText(browserType: string) {
  // 直接从后端下发的常量中获取，不要硬编码
  if (!browserType || browserType === '') {
    return '未知';
  }
  
  return browserType; // 后端已经返回了格式化后的文本
}

/**
 * 获取操作系统类型显示文本
 */
export function getOSTypeText(osType: string) {
  // 直接从后端下发的常量中获取，不要硬编码
  if (!osType || osType === '') {
    return '未知';
  }
  
  return osType; // 后端已经返回了格式化后的文本
} 